import { format } from "date-fns";

// Rank records are matched by (search_term, date) for every grid cell. Indexing
// them once keeps that lookup O(1) instead of scanning the whole array per cell.
// Each entry keeps its position in the source array so that lookups resolve to
// the same record Array.prototype.find used to return.
export const createRankLookup = (ranks) => {
  const lookup = new Map();

  (ranks || []).forEach((rank, order) => {
    const searchTerm = rank?.search_term;
    if (searchTerm === undefined || searchTerm === null) return;

    let ranksByDate = lookup.get(searchTerm);
    if (!ranksByDate) {
      ranksByDate = new Map();
      lookup.set(searchTerm, ranksByDate);
    }
    if (!ranksByDate.has(rank.date)) {
      ranksByDate.set(rank.date, { rank, order });
    }
  });

  return lookup;
};

export const getRankForDateKeys = (lookup, searchTerm, dateKeys) => {
  const ranksByDate = lookup.get(searchTerm);
  if (!ranksByDate) return undefined;

  let earliest;
  for (const dateKey of dateKeys) {
    const entry = ranksByDate.get(dateKey);
    if (entry && (!earliest || entry.order < earliest.order)) {
      earliest = entry;
    }
  }
  return earliest?.rank;
};

export const getFirstRankForTerm = (lookup, searchTerm) => {
  const ranksByDate = lookup.get(searchTerm);
  if (!ranksByDate) return undefined;

  const first = ranksByDate.values().next();
  return first.done ? undefined : first.value.rank;
};

const formatDayMonth = (date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "short" });
  return `${day} ${month}`;
};

// Every column resolves to a small set of candidate date strings, computed once
// per column rather than once per scanned rank record.
export const buildDateKeys = (allDates, periodicity) =>
  (allDates || []).map((date) => {
    if (periodicity === "week") {
      return [`${formatDayMonth(date[0]._d)} - ${formatDayMonth(date[1]._d)}`];
    }
    if (periodicity === "month") {
      const monthDate = date[0]._d;
      const month = monthDate.toLocaleString("en-US", { month: "short" });
      return [`${month} ${monthDate.getFullYear()}`];
    }
    return [format(date, "dd-MMM"), format(date, "d-MMM")];
  });

export const getUniqueSearchTerms = (ranks, sponsoredRanks) => [
  ...new Set([
    ...(ranks || []).map((rank) => rank?.search_term),
    ...(sponsoredRanks || []).map((rank) => rank?.search_term),
  ]),
];

const WEIGHTED_RANK_DATE_FORMATS = [
  "MM-dd",
  "M-dd",
  "MM-d",
  "M-d",
  "yyyy-MM-dd",
  "d MMM",
  "dd MMM",
  "d-MMM",
  "dd-MMM",
  "MMM d",
  "MMM dd",
];

export const createWeightedRankDateIndex = (dates) => {
  const index = new Map();
  (dates || []).forEach((date, dateIndex) => {
    if (!index.has(date)) {
      index.set(date, dateIndex);
    }
  });
  return index;
};

export const findWeightedRankDateIndex = (dateIndex, date) => {
  if (!dateIndex || dateIndex.size === 0) return -1;

  for (const dateFormat of WEIGHTED_RANK_DATE_FORMATS) {
    const matchedIndex = dateIndex.get(format(date, dateFormat));
    if (matchedIndex !== undefined) return matchedIndex;
  }
  return -1;
};

// Every field is quoted, matching the shape react-csv produced, but embedded
// quotes are doubled so keywords containing them cannot break the columns.
const toCsvField = (value) => {
  const stringValue =
    value === undefined || value === null ? "" : String(value);
  return `"${stringValue.replace(/"/g, '""')}"`;
};

const serializeCsv = (headers, rows) =>
  [
    headers.map((header) => toCsvField(header.label)).join(","),
    ...rows.map((row) =>
      headers.map((header) => toCsvField(row[header.key])).join(",")
    ),
  ].join("\n");

export const downloadCsv = ({ filename, headers, rows }) => {
  // Leading BOM keeps Excel from mangling non-ASCII keywords.
  const blob = new Blob(["\uFEFF", serializeCsv(headers, rows)], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// A rank cell is `{ position, ... }` where position is a number, a numeric
// string (weighted rank) or "-" when the ASIN was not found for that term.
export const parseRankPosition = (cellValue) => {
  const raw =
    cellValue && typeof cellValue === "object" ? cellValue.position : cellValue;
  if (raw === null || raw === undefined || raw === "" || raw === "-") {
    return null;
  }
  const position = Number(raw);
  return Number.isFinite(position) && position > 0 ? position : null;
};

// Sort comparator for the per-day rank columns. Ranked cells order by position;
// unranked cells stay at the bottom in both directions. The grid multiplies the
// result by -1 for a descending sort, so the unranked branch is pre-negated.
export const compareRankCells = (a, b, sortDirection = "asc") => {
  const positionA = parseRankPosition(a);
  const positionB = parseRankPosition(b);

  if (positionA === null || positionB === null) {
    if (positionA === positionB) return 0;
    const unrankedLast = positionA === null ? 1 : -1;
    return sortDirection === "desc" ? -unrankedLast : unrankedLast;
  }
  return positionA - positionB;
};

const normalizeKeyword = (keyword) =>
  String(keyword ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

// "apple, apple sauce" → the set {"apple", "apple sauce"}. Each entry has to
// match a keyword in full, so "apple" alone never brings up "apple sauce".
export const parseKeywordFilter = (input) =>
  new Set(
    String(input ?? "")
      .split(/[,\n]/)
      .map(normalizeKeyword)
      .filter((keyword) => keyword.length > 0)
  );

export const matchesKeywordFilter = (keyword, filterSet) =>
  !filterSet ||
  filterSet.size === 0 ||
  filterSet.has(normalizeKeyword(keyword));
